/**
 * @fileoverview AgentEandOManager - Container Component for E&O Insurance Management
 *
 * Main orchestrator component for E&O (Errors & Omissions) insurance module during agent onboarding.
 * Coordinates between policy type selection, form display, and validation callbacks.
 *
 * Component Hierarchy:
 * - AgentEandOManager (this file) - Container/orchestrator
 *   ├── EandOPolicySupplier - Policy type selector (Personal vs Company)
 *   └── EandOForm - Policy details form with PDF uploader
 *
 * Integration:
 * - Used within AgentProfileIntake during onboarding
 * - Connects to EopolicyStore (MobX) for state management
 * - Calls parent validation callback (onAutoSave) when validation state changes
 *
 * Responsibilities:
 * - Initialize store on component mount
 * - Display optional content via ContentHtml
 * - Render policy type selector (always visible)
 * - Conditionally render form (only after type selected)
 * - Trigger validation callbacks for parent component
 *
 * Related Documentation:
 * - /docs/modules/AgentEandOManager/components.md - Component details
 * - /docs/modules/AgentEandOManager/architecture.md - Data flow
 *
 * @module components/agents/AgentEandOManager
 * @requires react
 * @requires mobx-react-lite
 */

import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import EandOForm from './EandOForm.component'
import { MDBRow, MDBCol, MDBCollapse } from 'mdb-react-ui-kit'
import UserProfileService from './../../../shared/services/UserProfile.service'
import ContentHtml from './../../../components/content/ContentHtml/ContentHtml.component'
import EopolicyStore from './Eopolicy.store'
import EandOPolicySupplier from './EandOPolicySupplier.component'

import './AgentEandOManager.scss'

/**
 * AgentEandOManager - Container component for E&O insurance management
 *
 * Orchestrates E&O policy selection and form display during agent onboarding.
 * Initializes store, handles validation callbacks, and coordinates child components.
 *
 * Business Logic:
 * - Personal policies: Agent must complete all 7 fields + upload PDF
 * - Company policies: Auto-valid (no fields required from agent)
 * - Validation state communicated to parent via onAutoSave callback
 *
 * Component Lifecycle:
 * 1. Mount → useEffect fetches User → initUser(User)
 * 2. After init → calls onAutoSave with initial validation state
 * 3. User selects policy type → EandOPolicySupplier updates store
 * 4. Store update → component re-renders (MobX observer)
 * 5. Form appears → user completes fields → validation updates
 * 6. Each update → onAutoSave called with new validation state
 *
 * @component
 * @param {object} props - Component props
 * @param {string[]} [props.policyTypes=['personal','company']] - Allowed policy types based on user role
 * @param {boolean} [props.autoSave=false] - Enable auto-save in form (passed to EandOForm)
 * @param {function} [props.onAutoSave] - Validation callback: (isValid: boolean) => void
 * @param {string} [props.contentSlug] - Optional content slug for ContentHtml display
 *
 * @returns {JSX.Element} E&O manager UI with policy selector and form
 *
 * @example
 * // In AgentProfileIntake.component.jsx
 * <AgentEandOManager
 *   policyTypes={['personal']}  // Affiliate: personal only
 *   autoSave={true}
 *   onAutoSave={(isValid) => {
 *     Store.setValidity('eopolicy', isValid)
 *   }}
 *   contentSlug="eo-insurance-info"
 * />
 *
 * @example
 * // Signature agent (both options)
 * <AgentEandOManager
 *   policyTypes={['personal', 'company']}
 *   autoSave={true}
 *   onAutoSave={(isValid) => {
 *     Store.setValidity('eopolicy', isValid)
 *   }}
 * />
 */
const AgentEandOManager = props => {
  const onAutoSave = props?.onAutoSave && typeof props?.onAutoSave === 'function' ? props.onAutoSave : null,
    userId = UserProfileService.getUserId(),
    { initUser, getIsValid, policyType } = EopolicyStore

  useEffect(() => {
    UserProfileService.instance().then(User => {
      initUser(User).then(() => {
        // Trigger validation after user initialization
        if (onAutoSave && typeof onAutoSave === 'function') onAutoSave(policyType === 'company' || getIsValid())
      })
    })
  }, [userId, initUser, getIsValid, policyType, onAutoSave])

  return (
    <MDBRow id='AgentEandOManagerComponent'>
      <MDBCol size='12'>{props?.contentSlug ? <ContentHtml contentSlug={props.contentSlug} /> : <></>}</MDBCol>
      <MDBCol size='12'>
        <div className='eopolicy-form-wrapper'>
          <EandOPolicySupplier policyTypes={props?.policyTypes || []} />
          <MDBCollapse open={!!policyType}>
            {policyType ? (
              <>
                <br />
                <EandOForm autoSave={!!props?.autoSave} onAutoSave={props?.onAutoSave} />
              </>
            ) : (
              <></>
            )}
          </MDBCollapse>
        </div>
      </MDBCol>
    </MDBRow>
  )
}

export default observer(AgentEandOManager)
